// Per-tool task queues (UX6) — its own panel, directly under the Overview graph.
//
// The queue depths were a tab inside the Activity block: the one number that says
// "the fleet is behind" was three clicks and a tab bar away from the graph that
// shows WHICH node is behind. They belong next to each other, so this is a panel
// of its own now, and the graph above it draws the same figures on the nodes.
//
// The depths are scoped to the caller's networks server-side; only the destructive
// Drain column is admin's, because draining a shared tool's queue cancels
// everybody's work.

import { useState } from 'react';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { useAuth } from '../auth/AuthContext';
import { useSeries, seriesKeys } from '../store/store';
import { Loading, ErrorState } from './ui';
import { DataTable, type Column } from './DataTable';
import { ConfirmDialog } from './Modal';
import { Sparkline } from './Sparkline';
import { friendly } from '../views/Networks';
import type { QueueDepth } from '../api/schemas';

// ST39 — a per-tool queue trend sparkline (depth + running lines) off the live series.
// A STABLE 5-minute rolling window + a pinned 0..max value axis keep the line scrolling
// smoothly instead of rescaling (and jittering) on every point. The value ceiling rounds
// up to a floor of 4 so small integer counts don't rescale on every 0↔1↔2 change.
const QUEUE_SPARK_WINDOW_MS = 5 * 60_000;

function QueueSpark({ tool }: { tool: string }) {
  const [depth, running] = useSeries([seriesKeys.queueDepth(tool), seriesKeys.queueRunning(tool)]);
  const peak = Math.max(0, ...depth.points.map((p) => p.value), ...running.points.map((p) => p.value));
  const vMax = Math.max(4, Math.ceil(peak));
  return (
    <Sparkline
      width={120}
      height={28}
      min={0}
      max={vMax}
      windowMs={QUEUE_SPARK_WINDOW_MS}
      ariaLabel={`queue trend for ${tool}`}
      series={[
        { points: depth.points, color: 'var(--accent, #4f8cff)', label: 'depth' },
        { points: running.points, color: 'var(--ok, #3fb950)', label: 'running' },
      ]}
    />
  );
}

export function QueuesPanel() {
  const { isAdmin } = useAuth();
  const res = useResource(() => api.queues(), []);
  // Drain: the queue whose confirm dialog is open, plus the in-flight/error state.
  const [drainTool, setDrainTool] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function drain() {
    if (!drainTool) return;
    setBusy(true);
    setErr(null);
    try {
      await api.drainQueue(drainTool);
      setDrainTool(null);
      res.reload();
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  const cols: Column<QueueDepth>[] = [
    { key: 'tool', header: 'Tool', cell: (q) => <code className="mono">{q.canonical_tool}</code> },
    { key: 'depth', header: 'Queued', align: 'right', cell: (q) => <b>{q.depth}</b> },
    { key: 'running', header: 'Running', align: 'right', cell: (q) => q.running },
    { key: 'providers', header: 'Providers', align: 'right', cell: (q) => q.providers },
    { key: 'wait', header: 'Oldest wait', align: 'right', cell: (q) => (q.oldest_wait_s != null ? `${q.oldest_wait_s}s` : '—') },
    { key: 'trend', header: 'Trend (depth·running)', cell: (q) => <QueueSpark tool={q.canonical_tool} /> },
  ];
  if (isAdmin) {
    cols.push({
      key: 'drain',
      header: '',
      cell: (q) => (
        <button
          className="btn btn-sm btn-danger"
          data-testid="queue-drain"
          disabled={q.depth === 0}
          title={q.depth === 0 ? 'Queue is empty' : 'Cancel every queued task for this tool'}
          onClick={() => { setErr(null); setDrainTool(q.canonical_tool); }}
        >
          Drain
        </button>
      ),
    });
  }

  if (res.loading) return <Loading />;
  if (res.error || !res.data) return <ErrorState message={res.error ?? 'no data'} onRetry={res.reload} />;
  return (
    <>
      <DataTable columns={cols} rows={res.data.items} rowKey={(q) => q.canonical_tool} empty="No queues." />
      {drainTool && (
        <ConfirmDialog
          title="Drain queue"
          message={<>Cancel every QUEUED task for <code className="mono small">{drainTool}</code>? Tasks already running are not affected.</>}
          confirmLabel="Drain"
          busy={busy}
          error={err}
          onConfirm={drain}
          onClose={() => setDrainTool(null)}
        />
      )}
    </>
  );
}
